import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  ArrowUpDown,
  MoreHorizontal,
  Pencil,
  Copy,
  Send,
  Ban,
  Lock,
  CalendarDays,
  Move,
  PauseCircle,
  PlayCircle,
} from "lucide-react";
import { toast } from "sonner";

import AdminPageHeader from "@/components/layout/AdminPageHeader";
import EstadoEventoBadge from "@/components/common/EstadoEventoBadge";
import EmptyState from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { SCREENS, EVENTOS_ADMIN } from "@/constants/testIds";
import {
  aplicarFiltro,
  buscar,
  ESTADO_EVENTO,
  formatearFechaCorta,
  FILTROS_EVENTO,
  hidratarEventos,
  listarEventos,
  ordenarPorFecha,
} from "@/data/adminEventosStore";
import EventosAccionesDialogs from "@/screens/admin/eventos/EventosAccionesDialogs";
import { esUrlImagenSegura, fallbackImagenEvento, sanearUrlImagen } from "@/utils/imagenes";
import { getAdminSession } from "@/data/adminAuthStore";
import { puedeGestionarMesas } from "@/data/adminCambioMesaStore";

const puedeEditar = (ev) =>
  ev.estado !== ESTADO_EVENTO.EVENTO_CERRADO;
const puedeCerrar = (ev) => {
  if (ev.estado === ESTADO_EVENTO.EVENTO_CERRADO) return false;
  if (!ev.fecha) return false;
  const d = new Date(`${ev.fecha}T00:00:00`);
  return d <= new Date();
};

export default function AdminEventosListado() {
  const navigate = useNavigate();
  const [tick, setTick] = useState(0);
  const [query, setQuery] = useState("");
  const [filtro, setFiltro] = useState("todos");
  const [ordenDireccion, setOrdenDireccion] = useState("asc");
  const [dialog, setDialog] = useState(null);
  const sessionRol = getAdminSession()?.rol;
  const puedeMesas = puedeGestionarMesas(sessionRol);

  useEffect(() => {
    // Hidrata el cache local con la lista real del backend.
    hidratarEventos().then(() => setTick((t) => t + 1));
  }, []);

  const eventos = useMemo(() => listarEventos(), [tick]);

  const filtrados = useMemo(() => {
    const porFiltro = aplicarFiltro(eventos, filtro);
    const conBusqueda = buscar(porFiltro, query);
    return ordenarPorFecha(conBusqueda, ordenDireccion);
  }, [eventos, filtro, query, ordenDireccion]);

  const toggleOrden = () =>
    setOrdenDireccion((d) => (d === "asc" ? "desc" : "asc"));

  const abrirDialog = (tipo, evento) => setDialog({ tipo, evento });
  const cerrarDialog = () => setDialog(null);
  const trasAccion = (evNuevo, opts) => {
    setTick((t) => t + 1);
    cerrarDialog();
    if (opts && opts.goEdit) navigate(`/admin/eventos/${evNuevo.id}/editar`);
    if (opts && opts.goEditNew) navigate(`/admin/eventos/${opts.goEditNew}/editar`);
  };

  return (
    <div data-testid={SCREENS.adminEventos} className="flex flex-col gap-8">
      <AdminPageHeader
        eyebrow="Portal administrativo"
        title="Eventos"
        description="Administra el catálogo de eventos del Casino Tampiqueño."
      />

      {/* Barra de acciones */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6 justify-between">
        <div className="relative w-full lg:max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            data-testid={EVENTOS_ADMIN.search}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre, salón o tipo…"
            className="pl-9 h-10 bg-card"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid={EVENTOS_ADMIN.ordenFecha}
            onClick={toggleOrden}
            className="h-10 rounded-xl border-border/70 bg-card"
          >
            <ArrowUpDown className="w-4 h-4 mr-2" />
            Fecha {ordenDireccion === "asc" ? "↑" : "↓"}
          </Button>
          <Button
            asChild
            data-testid={EVENTOS_ADMIN.btnNuevo}
            className="h-10 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Link to="/admin/eventos/nuevo">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo evento
            </Link>
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2">
        {FILTROS_EVENTO.map((f) => {
          const active = filtro === f.id;
          return (
            <button
              type="button"
              key={f.id}
              data-testid={EVENTOS_ADMIN.filtro(f.id)}
              onClick={() => setFiltro(f.id)}
              className={cn(
                "px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors",
                active
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card text-muted-foreground border-border/70 hover:text-foreground hover:border-border",
              )}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Tabla / empty state */}
      {filtrados.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          eyebrow="Sin resultados"
          title="No hay eventos que coincidan"
          description="Ajusta la búsqueda o los filtros, o crea un nuevo evento."
          testId={EVENTOS_ADMIN.emptyState}
        />
      ) : (
        <div
          data-testid={EVENTOS_ADMIN.table}
          className="rounded-2xl border hairline bg-card overflow-hidden"
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-muted/40">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-16">Imagen</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="whitespace-nowrap">Fecha</TableHead>
                  <TableHead className="whitespace-nowrap">Hora</TableHead>
                  <TableHead>Salón</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="whitespace-nowrap">Venta</TableHead>
                  <TableHead className="text-right whitespace-nowrap">
                    Boletos vendidos
                  </TableHead>
                  <TableHead className="text-right whitespace-nowrap">
                    Capacidad
                  </TableHead>
                  <TableHead className="w-16 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtrados.map((ev) => (
                  <TableRow
                    key={ev.id}
                    data-testid={EVENTOS_ADMIN.row(ev.id)}
                    className="cursor-default"
                  >
                    <TableCell>
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted ring-1 ring-border/70">
                        {esUrlImagenSegura(ev.imagenPrincipal) ? (
                          <img
                            src={sanearUrlImagen(ev.imagenPrincipal)}
                            alt={ev.nombre}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={fallbackImagenEvento}
                          />
                        ) : (
                          <div className="w-full h-full grid place-items-center text-muted-foreground">
                            <CalendarDays className="w-5 h-5" strokeWidth={1.6} />
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[24rem]">
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/admin/eventos/${ev.id}/editar`)
                        }
                        className="text-left"
                      >
                        <p className="font-medium leading-tight truncate">
                          {ev.nombre}
                        </p>
                        {ev.subtitulo && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            {ev.subtitulo}
                          </p>
                        )}
                      </button>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {formatearFechaCorta(ev.fecha, ev.fechaLegible || "—")}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {ev.hora || ev.horaLegible || "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {ev.salon || "—"}
                    </TableCell>
                    <TableCell>
                      <EstadoEventoBadge estado={ev.estado} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {ev.tipoAcceso === "SOCIOS_INVITADOS"
                        ? "Socios e invitados"
                        : "Solo socios"}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {ev.boletosVendidos ?? 0}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {ev.capacidad ?? 0}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            aria-label="Acciones"
                            data-testid={EVENTOS_ADMIN.rowActions(ev.id)}
                            className="w-9 h-9 rounded-lg grid place-items-center hover:bg-muted transition-colors"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem
                            data-testid={EVENTOS_ADMIN.actionEditar(ev.id)}
                            disabled={!puedeEditar(ev)}
                            onClick={() =>
                              navigate(`/admin/eventos/${ev.id}/editar`)
                            }
                          >
                            <Pencil className="w-4 h-4" />
                            {puedeEditar(ev) ? "Editar" : "Ver (cerrado)"}
                          </DropdownMenuItem>
                          {puedeMesas && (
                            <DropdownMenuItem
                              data-testid={EVENTOS_ADMIN.actionGestionMesas(ev.id)}
                              onClick={() =>
                                navigate(
                                  `/admin/eventos/${ev.id}/cambio-mesa`
                                )
                              }
                            >
                              <Move className="w-4 h-4" />
                              Gestionar mesas
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            data-testid={EVENTOS_ADMIN.actionDuplicar(ev.id)}
                            onClick={() => abrirDialog("duplicar", ev)}
                          >
                            <Copy className="w-4 h-4" />
                            Duplicar
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            data-testid={EVENTOS_ADMIN.actionPublicar(ev.id)}
                            disabled={ev.estado !== ESTADO_EVENTO.BORRADOR}
                            onClick={() => abrirDialog("publicar", ev)}
                          >
                            <Send className="w-4 h-4" />
                            Publicar
                          </DropdownMenuItem>
                          {ev.estado === ESTADO_EVENTO.VENTA_ACTIVA && (
                            <DropdownMenuItem
                              data-testid={EVENTOS_ADMIN.actionPausar(ev.id)}
                              onClick={() => abrirDialog("pausar", ev)}
                            >
                              <PauseCircle className="w-4 h-4" />
                              Pausar venta
                            </DropdownMenuItem>
                          )}
                          {ev.estado === ESTADO_EVENTO.VENTA_PAUSADA && (
                            <DropdownMenuItem
                              data-testid={EVENTOS_ADMIN.actionReanudar(ev.id)}
                              onClick={() => abrirDialog("reanudar", ev)}
                            >
                              <PlayCircle className="w-4 h-4" />
                              Reanudar venta
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            data-testid={EVENTOS_ADMIN.actionCancelar(ev.id)}
                            disabled={
                              ev.estado === ESTADO_EVENTO.EVENTO_CERRADO ||
                              ev.estado === ESTADO_EVENTO.CANCELADO
                            }
                            onClick={() => abrirDialog("cancelar", ev)}
                          >
                            <Ban className="w-4 h-4" />
                            Cancelar
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            data-testid={EVENTOS_ADMIN.actionCerrar(ev.id)}
                            disabled={!puedeCerrar(ev)}
                            onClick={() => abrirDialog("cerrar", ev)}
                          >
                            <Lock className="w-4 h-4" />
                            Cerrar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Forzar refresh cuando volvemos del wizard */}
      <button
        aria-hidden
        tabIndex={-1}
        onClick={() => setTick((t) => t + 1)}
        className="sr-only"
      />

      <EventosAccionesDialogs
        dialog={dialog}
        onClose={cerrarDialog}
        onDone={trasAccion}
      />
    </div>
  );
}
